import { z } from 'zod'
import {
  SITE_CHANGE_ENTITIES,
  type SiteChangeAction,
  type SiteChangeEntity,
} from '../enums/site-change.enum.js'
import { VISIT_KINDS } from '../enums/visit-kind.enum.js'
import type { SiteChangeDto } from '../dto/site-history.dto.js'
import type {
  SiteRecordSnapshotOf,
  SiteSnapshotPatch,
} from '../dto/site-record-snapshot.js'
import type { SiteVisitDto } from '../dto/site-visit.dto.js'

/*
 * The site record's history: which of its fields are tracked, and which
 * changes to them ask "Was it wrong, or did it change?". Shared because the
 * phone decides when to ask and the server decides what an unanswered change
 * was, and the two must read the same rules. See docs/site-visits.md.
 */

/**
 * What a change to a tracked field is, once a site has been visited before.
 *
 * - `asked`: either, so the survey asks.
 * - `correction`: always the record having been wrong. A tree does not move
 *   or change species, and a name is the crew's, not the site's.
 * - `change`: always the site changing. A trunk measured again has grown.
 *
 * On a site's first look every change is a correction: there is no earlier
 * record for it to have changed from.
 */
export type FieldRule = 'asked' | 'correction' | 'change'

/**
 * The rules for one kind of record name exactly that snapshot's fields. A
 * field added to the snapshot and not here, or the other way around, fails
 * the assignment below.
 */
type FieldRulesFor<E extends SiteChangeEntity> = {
  readonly [K in keyof SiteRecordSnapshotOf<E>]: FieldRule
}

/**
 * The tracked fields of each part of the site record, and their rules. A
 * field left out (notes, a reading like static pressure, what a system was
 * said to have none of) changes without a history.
 */
export const SITE_RECORD_FIELDS = {
  BOUNDARY: { boundary: 'asked' },
  AREA: { name: 'correction', boundary: 'asked' },
  TREE: {
    label: 'correction',
    speciesId: 'correction',
    latitude: 'correction',
    longitude: 'correction',
    dbhInches: 'change',
  },
  WATER_SOURCE: {
    kind: 'correction',
    label: 'correction',
    latitude: 'asked',
    longitude: 'asked',
    meterSize: 'asked',
    horsepower: 'asked',
  },
  TIMER: {
    label: 'correction',
    latitude: 'asked',
    longitude: 'asked',
    partUuid: 'asked',
    waterSourceId: 'asked',
  },
  ZONE: {
    timerId: 'asked',
    number: 'asked',
    boundary: 'asked',
    headCounts: 'asked',
  },
  EQUIPMENT: {
    kind: 'correction',
    name: 'correction',
    latitude: 'asked',
    longitude: 'asked',
    partUuid: 'asked',
    decoderUuid: 'asked',
    zoneId: 'asked',
    timerId: 'asked',
    waterSourceId: 'asked',
    size: 'asked',
    backflowType: 'asked',
    serialNumber: 'asked',
    sensorType: 'asked',
    wellComponentType: 'asked',
    boxId: 'asked',
    spliceCount: 'asked',
  },
} as const satisfies { readonly [E in SiteChangeEntity]: FieldRulesFor<E> }

/** Every tracked field name, on any kind of record. */
export type AnyTrackedField = {
  [E in SiteChangeEntity]: keyof (typeof SITE_RECORD_FIELDS)[E] & string
}[SiteChangeEntity]

const ANY_TRACKED_FIELDS: ReadonlySet<string> = new Set(
  SITE_CHANGE_ENTITIES.flatMap((entity) =>
    Object.keys(SITE_RECORD_FIELDS[entity]),
  ),
)

/** Whether `field` is a tracked field of some kind of record. */
export function isAnyTrackedField(field: string): field is AnyTrackedField {
  return ANY_TRACKED_FIELDS.has(field)
}

/**
 * Whether adding or removing one asks, on a revisit: "New since last visit ·
 * Missed last time", "Gone · Recorded by mistake". An area is a ring the
 * office draws to name part of a site, so a new or deleted one is the record
 * being tidied, never the site changing.
 */
const ASKS_ON_ADD_OR_REMOVE: Record<SiteChangeEntity, boolean> = {
  BOUNDARY: false,
  AREA: false,
  TREE: true,
  WATER_SOURCE: true,
  TIMER: true,
  ZONE: true,
  EQUIPMENT: true,
}

/** One record's tracked fields, as they were or as they became. */
export type SiteRecordSnapshot<E extends SiteChangeEntity = SiteChangeEntity> =
  SiteRecordSnapshotOf<E>

/**
 * The snapshot schemas and the rules name the same fields. A field added to
 * one and not the other fails this assignment.
 */
type Align<E extends SiteChangeEntity> =
  keyof SiteRecordSnapshot<E> extends keyof (typeof SITE_RECORD_FIELDS)[E]
    ? keyof (typeof SITE_RECORD_FIELDS)[E] extends keyof SiteRecordSnapshot<E>
      ? true
      : { extraRule: E }
    : { extraSnapshot: E }

type AssertAlign = {
  [E in SiteChangeEntity]: Align<E>
}[SiteChangeEntity] extends true
  ? true
  : never

export const SITE_SNAPSHOT_KEYS_MATCH_RULES: AssertAlign = true

/**
 * A snapshot field as JSON. Callers that already know the record's kind read
 * the field from the snapshot; this is the boundary that turns that value
 * into JSON for a reader that handles every kind, and it checks the value
 * rather than trusting the field's name.
 */
const snapshotJsonSchema = z.json()
const snapshotRecordSchema = z.record(z.string(), snapshotJsonSchema)

export type SnapshotJson = z.infer<typeof snapshotJsonSchema>

/**
 * One field of a snapshot, as JSON. Missing, or a record that is not JSON,
 * is undefined.
 */
export function snapshotValue<E extends SiteChangeEntity>(
  record: SiteRecordSnapshot<E> | SiteSnapshotPatch<E>,
  field: keyof (typeof SITE_RECORD_FIELDS)[E] & string,
): SnapshotJson | undefined {
  const parsed = snapshotRecordSchema.safeParse(record)
  if (!parsed.success) return undefined
  return parsed.data[field]
}

/**
 * The tracked fields that differ between two snapshots of one record, in the
 * order the rules list them. Values are compared as JSON, so build both sides
 * the same way: coordinates and decimals rounded to their column's scale,
 * lists in a fixed order.
 */
export function changedFields<E extends SiteChangeEntity>(
  entity: E,
  before: SiteRecordSnapshot<E>,
  after: SiteRecordSnapshot<E>,
): (keyof (typeof SITE_RECORD_FIELDS)[E] & string)[] {
  return trackedFields(entity).filter(
    (field) =>
      JSON.stringify(snapshotValue(before, field)) !==
      JSON.stringify(snapshotValue(after, field)),
  )
}

/** The tracked fields of one kind of record, in the order the rules list them. */
export function trackedFields<E extends SiteChangeEntity>(
  entity: E,
): (keyof (typeof SITE_RECORD_FIELDS)[E] & string)[] {
  const rules: (typeof SITE_RECORD_FIELDS)[E] = SITE_RECORD_FIELDS[entity]
  return Object.keys(rules).filter(
    (key): key is keyof (typeof SITE_RECORD_FIELDS)[E] & string => key in rules,
  )
}

/** Whether `field` is one this kind of record tracks. */
export function isTrackedField<E extends SiteChangeEntity>(
  entity: E,
  field: string,
): field is keyof (typeof SITE_RECORD_FIELDS)[E] & string {
  return Object.prototype.hasOwnProperty.call(SITE_RECORD_FIELDS[entity], field)
}

/**
 * The rule for one field of one kind of record.
 *
 * A field this kind does not track is a correction: that is the safe side of
 * the question, and the only callers that can reach it are building a change
 * whose field list was not narrowed to this kind.
 */
function ruleOf(entity: SiteChangeEntity, field: string): FieldRule {
  switch (entity) {
    case 'BOUNDARY':
      return isTrackedField(entity, field)
        ? SITE_RECORD_FIELDS.BOUNDARY[field]
        : 'correction'
    case 'AREA':
      return isTrackedField(entity, field)
        ? SITE_RECORD_FIELDS.AREA[field]
        : 'correction'
    case 'TREE':
      return isTrackedField(entity, field)
        ? SITE_RECORD_FIELDS.TREE[field]
        : 'correction'
    case 'WATER_SOURCE':
      return isTrackedField(entity, field)
        ? SITE_RECORD_FIELDS.WATER_SOURCE[field]
        : 'correction'
    case 'TIMER':
      return isTrackedField(entity, field)
        ? SITE_RECORD_FIELDS.TIMER[field]
        : 'correction'
    case 'ZONE':
      return isTrackedField(entity, field)
        ? SITE_RECORD_FIELDS.ZONE[field]
        : 'correction'
    case 'EQUIPMENT':
      return isTrackedField(entity, field)
        ? SITE_RECORD_FIELDS.EQUIPMENT[field]
        : 'correction'
  }
}

/**
 * Which kind of visit looks at a part of the site record.
 *
 * A visit covers trees, irrigation, or both. The boundary and the areas are
 * the rest of the record, and no visit covers them on their own, so they are
 * `'site'`. Built from `VISIT_KINDS` so the two cannot grow apart.
 */
export const SITE_RECORD_KINDS = [...VISIT_KINDS, 'site'] as const

export type SiteRecordKind = (typeof SITE_RECORD_KINDS)[number]

export function kindOf(entity: SiteChangeEntity): SiteRecordKind {
  switch (entity) {
    case 'TREE':
      return 'trees'
    case 'BOUNDARY':
    case 'AREA':
      return 'site'
    case 'WATER_SOURCE':
    case 'TIMER':
    case 'ZONE':
    case 'EQUIPMENT':
      return 'irrigation'
  }
}

/** A visit, by what deciding a revisit needs of it. */
export type VisitForRevisit = Pick<
  SiteVisitDto,
  'startedOn' | 'coversTrees' | 'coversIrrigation'
>

/**
 * Whether a change is made on a revisit: the site had a visit looking at this
 * part of it before the one the change is made in. `since` is that visit's
 * day, or today's for a change made in none, so a desk edit the day after the
 * first visit asks and one during it does not.
 *
 * The boundary and areas belong to both kinds of visit.
 */
export function isRevisit(
  visits: readonly VisitForRevisit[],
  kind: SiteRecordKind,
  since: string,
): boolean {
  return visits.some(
    (visit) =>
      visit.startedOn < since &&
      (kind === 'site' ||
        (kind === 'trees' ? visit.coversTrees : visit.coversIrrigation)),
  )
}

/** A change to one record, as the survey is about to save it. */
export type PendingChange<E extends SiteChangeEntity = SiteChangeEntity> =
  | { action: 'ADDED' | 'REMOVED' }
  | {
      action: 'CHANGED'
      fields: readonly (keyof (typeof SITE_RECORD_FIELDS)[E] & string)[]
    }

/**
 * Whether the survey asks "Was it wrong, or did it change?" of a change, on
 * a revisit.
 *
 * A specific kind of record only accepts its own fields. A value that is
 * still the whole union of kinds accepts any tracked field, which is how a
 * caller asks the question of a pin move before it knows the kind.
 */
export function asksWhetherChanged<E extends SiteChangeEntity>(
  entity: E,
  change: PendingChange<E>,
): boolean
export function asksWhetherChanged(
  entity: SiteChangeEntity,
  change:
    | { action: 'ADDED' | 'REMOVED' }
    | { action: 'CHANGED'; fields: readonly AnyTrackedField[] },
): boolean
export function asksWhetherChanged(
  entity: SiteChangeEntity,
  change:
    | { action: 'ADDED' | 'REMOVED' }
    | { action: 'CHANGED'; fields: readonly string[] },
): boolean {
  if (change.action !== 'CHANGED') return ASKS_ON_ADD_OR_REMOVE[entity]
  return change.fields.some((field) => ruleOf(entity, field) === 'asked')
}

/**
 * Whether adding, removing or restoring one is a correction.
 *
 * `answer` is the survey's: true for "Missed last time" and "Recorded by
 * mistake". Left unanswered on a revisit (a build from before the question,
 * or a desk or an assistant that did not ask), an addition is the site
 * changing and a removal is a mistake, which is what deleting meant before
 * there was a choice. Restoring undoes a removal that should not have been.
 */
export function isCorrectionOf(
  entity: SiteChangeEntity,
  action: Exclude<SiteChangeAction, 'CHANGED'>,
  revisit: boolean,
  answer: boolean | undefined,
): boolean {
  if (!revisit || action === 'RESTORED') return true
  if (!ASKS_ON_ADD_OR_REMOVE[entity]) return true
  return answer ?? action === 'REMOVED'
}

/**
 * Split a change's fields into the correction and the real change, by each
 * field's rule. An asked field takes the answer, and unanswered is a change:
 * a change wrongly logged is history somebody can see and question, and a
 * correction wrongly logged rewrites every past map without a trace.
 */
export function splitChange<E extends SiteChangeEntity>(
  entity: E,
  fields: readonly (keyof (typeof SITE_RECORD_FIELDS)[E] & string)[],
  revisit: boolean,
  answer: boolean | undefined,
): {
  corrected: (keyof (typeof SITE_RECORD_FIELDS)[E] & string)[]
  changed: (keyof (typeof SITE_RECORD_FIELDS)[E] & string)[]
} {
  const corrected: (keyof (typeof SITE_RECORD_FIELDS)[E] & string)[] = []
  const changed: (keyof (typeof SITE_RECORD_FIELDS)[E] & string)[] = []
  for (const field of fields) {
    const rule = ruleOf(entity, field)
    const isCorrection =
      !revisit || rule === 'correction' || (rule === 'asked' && answer === true)
    if (isCorrection) {
      corrected.push(field)
    } else {
      changed.push(field)
    }
  }
  return { corrected, changed }
}

/**
 * What the rules below read of a logged change.
 *
 * Indexed off `SiteChangeDto` rather than picked, because a pick of that
 * union is a union, and these fields do not vary by kind. A field removed
 * from the change, or given a different type, fails here.
 */
export type LoggedChange = {
  id: SiteChangeDto['id']
  entity: SiteChangeDto['entity']
  entityId: SiteChangeDto['entityId']
  action: SiteChangeDto['action']
  isCorrection: SiteChangeDto['isCorrection']
  /** When it happened in the world, `YYYY-MM-DD`. */
  effectiveOn: SiteChangeDto['effectiveOn']
  /** When it was logged, as an ISO instant: what orders a put-back after its removal. */
  recordedAt: SiteChangeDto['recordedAt']
}

/**
 * The removals a later "put back" undid. Putting a record back always means
 * its removal was wrong, so the removal is not news about the site: it
 * counts as a correction, as the put-back does.
 */
export function takenBackRemovals(
  changes: readonly LoggedChange[],
): Set<string> {
  const undone = new Set<string>()
  for (const change of changes) {
    if (change.action !== 'REMOVED') continue
    const restored = changes.some(
      (later) =>
        later.action === 'RESTORED' &&
        later.entity === change.entity &&
        later.entityId === change.entityId &&
        later.recordedAt > change.recordedAt,
    )
    if (restored) undone.add(change.id)
  }
  return undone
}

/**
 * The changes that are the site really changing: not corrections, which
 * apply all the way back, and not removals a later put-back undid. What the
 * timeline shows by default, what "since our last visit" tells a client, and
 * what the map as of a past visit undoes.
 */
export function realChanges<C extends LoggedChange>(
  changes: readonly C[],
): C[] {
  const undone = takenBackRemovals(changes)
  return changes.filter(
    (change) => !change.isCorrection && !undone.has(change.id),
  )
}
